import { useEffect, useMemo, useState } from 'react';
import { type MapLayerMouseEvent, Popup, useMap } from 'react-map-gl';
import type { RejectedPlace } from './coverage-preview';
import { LTD_PROPERTY, REJECTED_LAYER_IDS } from './rejected-place-utils';
import styles from './case-coverage-map.module.scss';

interface Hover {
  longitude: number;
  latitude: number;
  text: string;
}

/**
 * The tooltip over a rejected place: its reason, as the slot grid gives it. Listens on the rejected
 * layers only, through the map itself, so the map's own popups are left as they are.
 *
 * @param {object} props - The rejected places.
 * @param {Array} props.places - The places, by IUFS row id, with their reason.
 * @return {JSX.Element | null} The popup at the pointer, while it is over one.
 */
export function RejectedPlaceTooltip({
  places,
}: {
  places: readonly RejectedPlace[];
}): JSX.Element | null {
  const { current: mapRef } = useMap();
  const [hover, setHover] = useState<Hover | null>(null);
  const reasons = useMemo(
    () => new Map(places.map((place) => [place.iufsId, place.reason])),
    [places]
  );

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return undefined;

    /**
     * Follows the pointer over a rejected place with its tooltip.
     *
     * @param {MapLayerMouseEvent} event - The move, with the features under the pointer.
     */
    const handleMove = (event: MapLayerMouseEvent) => {
      const reason = reasons.get(
        Number(event.features?.[0]?.properties?.[LTD_PROPERTY])
      );
      if (!reason) {
        setHover(null);
        return;
      }
      setHover({
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
        text: reason,
      });
    };
    /** Hides the tooltip as the pointer leaves the place. */
    const handleLeave = () => setHover(null);

    REJECTED_LAYER_IDS.forEach((layerId) => {
      map.on('mousemove', layerId, handleMove);
      map.on('mouseleave', layerId, handleLeave);
    });
    return () => {
      REJECTED_LAYER_IDS.forEach((layerId) => {
        map.off('mousemove', layerId, handleMove);
        map.off('mouseleave', layerId, handleLeave);
      });
    };
  }, [mapRef, reasons]);

  if (!hover) return null;
  return (
    <Popup
      longitude={hover.longitude}
      latitude={hover.latitude}
      closeButton={false}
      closeOnClick={false}
      offset={12}
      className={styles.rejectedPopup}
    >
      {hover.text}
    </Popup>
  );
}
